import { Inject, Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { cloneDeep } from 'lodash-es';
import { ENVIRONMENT_TOKEN } from '../../../injection.tokens';
import { SeatTranslatorService } from '../../../service/seat/seat-translator.service';
import { ModifierTypeEnum } from '../../enum/modifier-type.enum';
import { OrderViewModel } from '../../view-model/order/order.view.model';
import { ScreeningItemExtraFeeViewModel } from '../../view-model/order/screening-item/screening-item-extra-fee.view.model';
import { ScreeningItemViewModel } from '../../view-model/order/screening-item/screening-item.view.model';
import { ScreenViewModel } from '../../view-model/screen/screen.view.model';
import { ScreeningViewModel } from '../../view-model/screening/screening.view.model';
import { TicketExtraFeeViewModel } from '../../view-model/shared/ticket/ticket-extra-fee.view.model';
import { TicketViewModel } from '../../view-model/shared/ticket/ticket.view.model';
import { VoucherTypeModel } from '../../voucher-type.model';
import { CateringAggregationArticleViewModel } from '../../catering/catering-aggregation.view.model';
import { OrderSummaryArticleCombinationItemViewModel } from './order-summary-article-combination-item.view.model';
import { OrderSummaryGiftCardItemViewModel } from './order-summary-gift-card-item.view.model';
import { OrderSummaryItemViewModel } from './order-summary-item.view.model';
import { OrderSummaryPersonalViewModel } from './order-summary-personal.view.model';
import { OrderSummaryVoucherItemViewModel } from './order-summary-voucher-item.view.model';
import { OrderSummaryViewModel } from './order-summary.view.model';
import { VoucherItemViewModel } from '../../view-model/order/voucher-item/voucher-item.view.model';
import { TotalizerService } from '../../../service/totalizer.service';
import { TicketExtraFeeApiModel } from '../../api-model/shared/ticket/ticket-extra-fee.api.model';
import { GadgetItemViewModel } from '../../view-model/order/gadget-item/gadget-item.view.model';

@Injectable({
  providedIn: 'root'
})
export class OrderSummaryBuilder {
  private skippingRomanDigitsInRows = false;
  private skippingRomanDigitsInCols = false;

  private order: OrderViewModel;
  private ticketList: Array<TicketViewModel>;
  private voucherTypeList: Array<VoucherTypeModel>;
  private cateringArticleCombinationList: Array<CateringAggregationArticleViewModel>;
  private screeningId: string;
  private screen: ScreenViewModel = null;
  private screening: ScreeningViewModel = null;
  private useOrderItemPrice = false;

  constructor(
    @Inject(ENVIRONMENT_TOKEN) protected environment: any,
    private translateService: TranslateService,
    private totalizerService: TotalizerService
  ) {}

  getOrderSummary(
    order: OrderViewModel,
    ticketList: Array<TicketViewModel>,
    voucherTypeList: Array<VoucherTypeModel>,
    cateringArticleCombinationList: Array<CateringAggregationArticleViewModel>,
    screeningId: string,
    screen: ScreenViewModel = null,
    screening: ScreeningViewModel = null,
    useOrderItemPrice = false
  ): OrderSummaryViewModel {
    this.order = order;
    this.ticketList = ticketList;
    this.voucherTypeList = voucherTypeList;
    this.cateringArticleCombinationList = cateringArticleCombinationList;
    this.screeningId = screeningId;
    this.screen = screen;
    this.screening = screening;
    this.useOrderItemPrice = useOrderItemPrice;

    let orderSummaryViewModel: OrderSummaryViewModel = new OrderSummaryViewModel();
    if (this.screen && this.screen.seats) {
      const seats = this.screen.seats;
      this.skippingRomanDigitsInRows = SeatTranslatorService.isNotRomanDigit(seats.map((elem) => elem.rowNumber));
      this.skippingRomanDigitsInCols = SeatTranslatorService.isNotRomanDigit(seats.map((elem) => elem.legendCol));
    }

    if (this.order) {
      let itemsWithoutVoucher: Array<ScreeningItemViewModel> = this.order.screeningItems.filter((x) => !x.hasVoucher());
      let itemsWithVoucher = this.order.screeningItems.filter((x) => x.hasVoucher());

      if (this.screeningId) {
        itemsWithoutVoucher = itemsWithoutVoucher.filter((x) => x.screeningId === this.screeningId);
        itemsWithVoucher = itemsWithVoucher.filter((x) => x.screeningId === this.screeningId);
      }
      orderSummaryViewModel.items = [];

      this.fillModelItems(itemsWithoutVoucher, false, orderSummaryViewModel);
      this.fillModelItems(itemsWithVoucher, true, orderSummaryViewModel);
      this.fillGadgetItems(this.order.gadgetItems, orderSummaryViewModel);
      this.fillVoucherItems(this.order.voucherItems, orderSummaryViewModel);
    }

    this.sortSeats(orderSummaryViewModel);

    if (this.voucherTypeList) {
      this.voucherTypeList.forEach((voucherItem) => {
        let summaryItem: OrderSummaryVoucherItemViewModel = orderSummaryViewModel.voucherItems.find((x) => x.voucher.id === voucherItem.id);

        if (!summaryItem) {
          summaryItem = new OrderSummaryVoucherItemViewModel();
          summaryItem.voucher = voucherItem;
          summaryItem.quantity = 1;
          orderSummaryViewModel.voucherItems.push(summaryItem);
        } else {
          summaryItem.quantity++;
        }

        orderSummaryViewModel.taxPrice += voucherItem.taxPrice;
        orderSummaryViewModel.totalPrice += voucherItem.price;
        orderSummaryViewModel.subTotalPrice += voucherItem.price;
      });
    }

    this.order?.cardItems?.forEach((card) => {
      const taxRate = this.order.cardTypeItems.find((type) => type.id === card.cardTypeId)?.taxRate ?? 0;

      orderSummaryViewModel.giftCardItems.push(new OrderSummaryGiftCardItemViewModel(card));
      orderSummaryViewModel.taxPrice += card.value - card.value / (1.0 + taxRate / 100.0);
      orderSummaryViewModel.totalPrice += card.value;
      orderSummaryViewModel.subTotalPrice += card.value;
    });

    if (this.cateringArticleCombinationList) {
      for (const article of this.cateringArticleCombinationList) {
        let summaryItem: OrderSummaryArticleCombinationItemViewModel = orderSummaryViewModel.cateringItems.find(
          (x) =>
            x.articleCombination.selectedCombinationHash === article.selectedCombinationHash &&
            x.articleCombination.voucherNumber === article.voucherNumber &&
            x.articleCombination.voucherName === article.voucherName
        );

        if (!summaryItem) {
          summaryItem = new OrderSummaryArticleCombinationItemViewModel();
          summaryItem.articleCombination = article;
          summaryItem.price = article.price;
          summaryItem.unitPriceWithSeparatedItems = summaryItem.price;
          summaryItem.unitPriceWithoutSeparatedItems = summaryItem.price;
          summaryItem.quantity = article.selectedQuantity;
          const selectedModifierItemsName: Array<string> = new Array<string>();

          article.modifierArticleList.forEach((modifier) => {
            let selectedModifierItemName = '';

            modifier.itemCollection.forEach((modifierItem) => {
              selectedModifierItemName = modifierItem.quantity > 1 ? `${modifierItem.name} (x${modifierItem.quantity})` : modifierItem.name;

              if (modifier.type === ModifierTypeEnum.NO_TYPE) {
                selectedModifierItemName = `${this.translateService.instant('catering.article.modifier.type.no_type')} ${selectedModifierItemName}`;
              }

              if (!modifier.separateItem) {
                selectedModifierItemsName.push(selectedModifierItemName);
              } else {
                summaryItem.unitPriceWithSeparatedItems += modifierItem.price * modifierItem.quantity;
              }
            });
          });

          article.subArticleList.forEach((subArticle) => {
            let selectedSubArticleItemName = subArticle.name;

            subArticle.replacementList.forEach((subArticleReplacer) => {
              selectedSubArticleItemName = subArticleReplacer.name;
            });

            selectedModifierItemsName.push(selectedSubArticleItemName);
          });

          selectedModifierItemsName.sort((a, b) => {
            return a.toLowerCase().localeCompare(b.toLowerCase());
          });

          summaryItem.description = selectedModifierItemsName.join('\u200e, ');
          summaryItem.price *= article.selectedQuantity;

          orderSummaryViewModel.cateringItems.push(summaryItem);
        } else {
          summaryItem.price += summaryItem.unitPriceWithoutSeparatedItems * article.selectedQuantity;
          summaryItem.quantity += article.selectedQuantity;
        }
        orderSummaryViewModel.totalPrice += summaryItem.unitPriceWithSeparatedItems * article.selectedQuantity;
      }
    }

    orderSummaryViewModel.personal = new OrderSummaryPersonalViewModel(this.order);

    if (this.screen) {
      orderSummaryViewModel.screen = this.screen;
    }

    if (this.screening) {
      orderSummaryViewModel.screening = this.screening;
    }

    return orderSummaryViewModel;
  }

  private fillModelItems(orderItems: ScreeningItemViewModel[], isVoucherItem: boolean, orderSummaryViewModel: OrderSummaryViewModel) {
    const ticketList = this.ticketList ?? [];
    const items = isVoucherItem ? orderSummaryViewModel.orderVoucherItems : orderSummaryViewModel.items;

    const itemVerify = (i, clonedTicketItem) => {
      const result =
        i.ticket.name === clonedTicketItem.name &&
        i.ticket.id === clonedTicketItem.id &&
        i.ticket.price === clonedTicketItem.price &&
        i.ticket.voucherNumber === clonedTicketItem.voucherNumber &&
        i.ticket.voucherName === clonedTicketItem.voucherName &&
        ((i.ticket.extraFees.length === 0 && clonedTicketItem.extraFees.length === 0) ||
          i.ticket.extraFees.some((ef) => clonedTicketItem.extraFees.some((e) => ef.extraFeeId === e.extraFeeId && ef.price === e.price)));
      return result;
    };

    orderItems.forEach((item: ScreeningItemViewModel) => {
      const clonedTicketItem: TicketViewModel = this.createTicketViewModel(item, ticketList);

      let summaryItem = items.find((i) => itemVerify(i, clonedTicketItem));
      let allItemSummary = orderSummaryViewModel.allItems.find((i) => itemVerify(i, clonedTicketItem));

      if (summaryItem === undefined) {
        summaryItem = new OrderSummaryItemViewModel(clonedTicketItem, 1);
        items.push(summaryItem);
      } else {
        summaryItem.quantity += 1;
      }

      if (this.screen && this.screen.pseats) {
        summaryItem.ticketSymbols.push(this.generateSeatNumber(clonedTicketItem.seatId));
      }

      if (allItemSummary === undefined) {
        allItemSummary = new OrderSummaryItemViewModel(clonedTicketItem, 1);
        orderSummaryViewModel.allItems.push(allItemSummary);
      } else {
        allItemSummary.quantity += 1;
      }

      orderSummaryViewModel.taxPrice += clonedTicketItem.priceWithExtraFee - clonedTicketItem.priceWithExtraFee / (1.0 + clonedTicketItem.tax / 100.0);
      orderSummaryViewModel.totalPrice += clonedTicketItem.priceWithExtraFee;
      orderSummaryViewModel.subTotalPrice += allItemSummary.ticket.priceWithExtraFee;
    });
  }

  private createTicketViewModel(item: ScreeningItemViewModel, ticketList: TicketViewModel[]): TicketViewModel {
    const matchingTickets = ticketList.filter((t) => t.screeningItemId === item.id);
    const ticketItem = matchingTickets.find((t) => t.id === item.ticketId) || matchingTickets[0];
    const clonedTicketItem: TicketViewModel = ticketItem ? cloneDeep(ticketItem) : new TicketViewModel();

    if (this.order.createdFromSalesDocument) {
      clonedTicketItem.name = item.name;
      clonedTicketItem.price = item.price;
      clonedTicketItem.oldPrice = item.price;
      clonedTicketItem.voucherName = item.voucherName;
      clonedTicketItem.voucherNumber = item.voucherNumber;
      clonedTicketItem.isVouchered = item.hasVoucher();

      clonedTicketItem.row = item.row;
      clonedTicketItem.seat = item.seat;
      clonedTicketItem.extraFees = item.extraFees.map((extraFee: ScreeningItemExtraFeeViewModel) => {
        const fee = new TicketExtraFeeViewModel({
          id: extraFee.name,
          extraFeeId: extraFee.name,
          name: extraFee.name,
          isOptional: extraFee.isOptional,
          defaultPriceLevelPrice: extraFee.defaultPriceLevelPrice,
          price: extraFee.price
        } as TicketExtraFeeApiModel);
        return fee;
      });
    } else {
      clonedTicketItem.oldPrice = clonedTicketItem.price;
      if (item.hasVoucher()) {
        clonedTicketItem.id = item.ticketId;
        clonedTicketItem.oldPrice = item.price;
        clonedTicketItem.voucherNumber = item.voucherNumber;
        //clonedTicketItem.name = item.name;
        clonedTicketItem.voucherName = item.voucherName;
        clonedTicketItem.isVouchered = true;
        clonedTicketItem.price = item.ticketPrice;
      }

      clonedTicketItem.extraFees = clonedTicketItem.extraFees.map((ef) => {
        const foundExtraFeeInItem = item.extraFees.find((efi) => efi.id === ef.extraFeeId);
        if (foundExtraFeeInItem) {
          ef.price = foundExtraFeeInItem.price;
        }

        return ef;
      });
    }

    if (this.useOrderItemPrice) {
      clonedTicketItem.price = item.ticketPrice;
    }
    clonedTicketItem.seatId = item.seatId;
    return clonedTicketItem;
  }

  private fillGadgetItems(gadgetItems: GadgetItemViewModel[], orderSummaryViewModel: OrderSummaryViewModel) {
    gadgetItems?.forEach((item: GadgetItemViewModel) => {
      orderSummaryViewModel.gadgetItems.push(item);
      orderSummaryViewModel.totalPrice += item.price;
      orderSummaryViewModel.subTotalPrice += item.price;
    });
  }

  private fillVoucherItems(voucherItems: VoucherItemViewModel[], orderSummaryViewModel: OrderSummaryViewModel) {
    voucherItems.forEach((item: VoucherItemViewModel) => {
      const model: OrderSummaryVoucherItemViewModel = new OrderSummaryVoucherItemViewModel();
      model.quantity = item.quantity;
      const voucherType: VoucherTypeModel = new VoucherTypeModel();
      voucherType.id = item.id;
      voucherType.itemId = item.itemId;
      voucherType.name = item.itemName;
      voucherType.price = item.itemPrice;
      voucherType.taxPrice;
      voucherType.taxRate = item.itemTaxRate;
      model.voucher = voucherType;

      orderSummaryViewModel.voucherItems.push(model);

      orderSummaryViewModel.totalPrice += model.voucher.price;
      orderSummaryViewModel.subTotalPrice += model.voucher.price;
    });
  }

  private generateSeatNumber(seatId: string): (string | number)[] {
    const seat = this.screen.findSeatByIdFlat(seatId);
    return seat
      ? [
          seat.rowNumber,
          seat.legendCol,
          SeatTranslatorService.translateValue(seat.rowNumber, this.skippingRomanDigitsInRows),
          SeatTranslatorService.translateValue(seat.legendCol, this.skippingRomanDigitsInCols)
        ]
      : [];
  }

  private sortSeats(orderSummaryViewModel: OrderSummaryViewModel): void {
    orderSummaryViewModel.items.forEach((elem) => {
      elem.ticketSymbols.sort((row, seat) => {
        // @ts-ignore
        return row[2] - seat[2] || row[3] - seat[3];
      });
    });
  }
}
